/**
 * Centralized API Error Handling
 * 
 * Provides unified error types, handling, classification, and user-friendly messages.
 * Consolidates error handling from multiple sources into single source of truth.
 */

import { PostgrestError } from '@supabase/supabase-js';
import * as Sentry from '@sentry/react';
import { logger } from '@/lib/services';
import { toast } from 'sonner';

// ============= Error Types =============

export type ApiErrorCode =
  | 'network_error'
  | 'timeout'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'validation_error'
  | 'conflict'
  | 'rate_limited'
  | 'server_error'
  | 'unknown';

export type ErrorCategory = 'fatal_ui_error' | 'data_error' | 'permission_error' | 'network_error' | 'validation_error';

export interface ApiError extends Error {
  code: ApiErrorCode;
  statusCode?: number;
  originalError?: unknown;
  context?: Record<string, unknown>;
  retryable: boolean;
}

export interface ClassifiedError {
  category: ErrorCategory;
  shouldCrashBoundary: boolean;
  userMessage: string;
  technicalMessage: string;
  errorCode: string;
  severity: 'info' | 'warn' | 'error' | 'critical';
}

// ============= Error Messages =============

const ERROR_MESSAGES: Record<ApiErrorCode, string> = {
  network_error: 'Нет соединения с сервером. Проверьте интернет.',
  timeout: 'Сервер не отвечает. Попробуйте позже.',
  unauthorized: 'Требуется авторизация. Войдите в аккаунт.',
  forbidden: 'У вас нет доступа к этому действию.',
  not_found: 'Запрашиваемые данные не найдены.',
  validation_error: 'Проверьте введённые данные.',
  conflict: 'Данные уже существуют или были изменены.',
  rate_limited: 'Слишком много запросов. Подождите минуту.',
  server_error: 'Ошибка сервера. Попробуйте позже.',
  unknown: 'Произошла непредвиденная ошибка.',
};

const RETRYABLE_CODES: ApiErrorCode[] = [
  'network_error',
  'timeout',
  'server_error',
  'rate_limited',
];

// ============= Pattern Matching for Classification =============

const FATAL_UI_PATTERNS = [
  'invariant violation',
  'cannot read properties of undefined',
  'cannot read properties of null',
  'is not a function',
  'is not defined',
  'render error',
  'maximum update depth exceeded',
  'too many re-renders',
];

const PERMISSION_PATTERNS = [
  'permission denied',
  'unauthorized',
  '401',
  '403',
  'row-level security',
  'rls policy',
  'access denied',
];

const NETWORK_PATTERNS = [
  'network error',
  'failed to fetch',
  'network request failed',
  'timeout',
  '500',
  '502',
  '503',
  '504',
  'econnrefused',
  'econnreset',
];

const DATA_PATTERNS = [
  'not found',
  '404',
  'no data',
  'empty result',
  'null value',
  'constraint violation',
  'foreign key',
  'duplicate key',
];

const RATE_LIMIT_PATTERNS = [
  /security purposes.*?(\d+)\s*seconds/i,
  /after\s+(\d+)\s*seconds/i,
  /every\s+(\d+)\s*seconds/i,
  /(\d+)\s*seconds/i,
];

// ============= Error Factory =============

export function createApiError(
  code: ApiErrorCode,
  message?: string,
  originalError?: unknown,
  context?: Record<string, unknown>
): ApiError {
  const error = new Error(message || ERROR_MESSAGES[code]) as ApiError;
  error.name = 'ApiError';
  error.code = code;
  error.originalError = originalError;
  error.context = context;
  error.retryable = RETRYABLE_CODES.includes(code);
  return error;
}

// ============= Error Classification =============

export function classifyHttpError(status: number): ApiErrorCode {
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 422 || status === 400) return 'validation_error';
  if (status === 429) return 'rate_limited';
  if (status >= 500) return 'server_error';
  return 'unknown';
}

export function classifyPostgrestError(error: PostgrestError): ApiErrorCode {
  const { code, message } = error;
  
  if (code === 'PGRST301' || message?.includes('JWT')) return 'unauthorized';
  if (code === '42501' || message?.includes('permission denied')) return 'forbidden';
  if (code === 'PGRST116' || message?.includes('not found')) return 'not_found';
  if (code === '23505' || message?.includes('duplicate')) return 'conflict';
  if (code === '23503' || message?.includes('violates')) return 'validation_error';
  if (code === '42P01' || code?.startsWith('42')) return 'server_error';
  
  return 'unknown';
}

/**
 * Classify error for API handling (React Query, mutations, etc.)
 */
export function classifyError(error: unknown): ApiError {
  // Already an ApiError
  if (isApiError(error)) {
    return error;
  }

  // Network/Fetch errors
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return createApiError('network_error', undefined, error);
  }

  // Timeout
  if (error instanceof DOMException && error.name === 'AbortError') {
    return createApiError('timeout', undefined, error);
  }

  // PostgrestError from Supabase
  if (isPostgrestError(error)) {
    const code = classifyPostgrestError(error);
    return createApiError(code, error.message, error);
  }

  // Response-like errors
  if (hasStatusCode(error)) {
    const code = classifyHttpError(error.status || error.statusCode);
    const message = error.message || error.error || ERROR_MESSAGES[code];
    const apiError = createApiError(code, message, error);
    apiError.statusCode = error.status || error.statusCode;
    return apiError;
  }

  // Generic Error
  if (error instanceof Error) {
    return createApiError('unknown', error.message, error);
  }

  // Unknown
  return createApiError('unknown', String(error), error);
}

/**
 * Classify error for ErrorBoundary (determines if should crash UI)
 */
export function classifyErrorForBoundary(error: unknown): ClassifiedError {
  const message = extractErrorMessage(error).toLowerCase();
  const name = error instanceof Error ? error.name : 'Error';
  
  // Check for fatal UI errors first (should crash boundary)
  if (isFatalUIError(message, name)) {
    return {
      category: 'fatal_ui_error',
      shouldCrashBoundary: true,
      userMessage: 'Произошла критическая ошибка интерфейса',
      technicalMessage: extractErrorMessage(error),
      errorCode: 'fatal_ui_error',
      severity: 'critical',
    };
  }
  
  // Permission errors
  if (matchesPatterns(message, PERMISSION_PATTERNS)) {
    return {
      category: 'permission_error',
      shouldCrashBoundary: false,
      userMessage: 'Недостаточно прав для выполнения операции',
      technicalMessage: extractErrorMessage(error),
      errorCode: 'permission_denied',
      severity: 'warn',
    };
  }
  
  // Network errors
  if (matchesPatterns(message, NETWORK_PATTERNS)) {
    return {
      category: 'network_error',
      shouldCrashBoundary: false,
      userMessage: 'Ошибка сети. Проверьте подключение к интернету',
      technicalMessage: extractErrorMessage(error),
      errorCode: 'network_error',
      severity: 'warn',
    };
  }
  
  // Data errors
  if (matchesPatterns(message, DATA_PATTERNS)) {
    return {
      category: 'data_error',
      shouldCrashBoundary: false,
      userMessage: 'Данные не найдены или повреждены',
      technicalMessage: extractErrorMessage(error),
      errorCode: 'data_error',
      severity: 'error',
    };
  }
  
  // Default: treat as data error (non-fatal)
  return {
    category: 'data_error',
    shouldCrashBoundary: false,
    userMessage: 'Произошла ошибка. Попробуйте ещё раз',
    technicalMessage: extractErrorMessage(error),
    errorCode: 'unknown_error',
    severity: 'error',
  };
}

// ============= Rate Limit Helpers =============

/**
 * Extract seconds from rate limit error message
 */
export function extractSecondsFromMessage(message: string): number | null {
  for (const pattern of RATE_LIMIT_PATTERNS) {
    const match = message.match(pattern);
    if (match) {
      return parseInt(match[1], 10);
    }
  }
  return null;
}

/**
 * Check if error is a rate limit (429) error
 */
export function isRateLimitError(error: unknown): boolean {
  if (!error) return false;
  
  const message = extractErrorMessage(error).toLowerCase();
  const status = hasStatusCode(error) ? (error.status || error.statusCode) : undefined;
  
  return status === 429 || 
         message.includes('rate limit') ||
         message.includes('too many') ||
         /security purposes.*seconds/i.test(message);
}

// ============= Helper Functions =============

function isFatalUIError(message: string, name: string): boolean {
  if (name === 'Invariant Violation') return true;
  return matchesPatterns(message, FATAL_UI_PATTERNS);
}

function matchesPatterns(message: string, patterns: string[]): boolean {
  return patterns.some(pattern => message.includes(pattern));
}

export function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'Unknown error';
}

// ============= Type Guards =============

export function isApiError(error: unknown): error is ApiError {
  return error instanceof Error && 'code' in error && 'retryable' in error;
}

export function isPostgrestError(error: unknown): error is PostgrestError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'message' in error &&
    'details' in error
  );
}

function hasStatusCode(error: unknown): error is { status?: number; statusCode?: number; message?: string; error?: string } {
  return (
    typeof error === 'object' &&
    error !== null &&
    ('status' in error || 'statusCode' in error)
  );
}

/**
 * Check if error should be shown as toast vs fallback UI
 */
export function shouldShowToast(classified: ClassifiedError): boolean {
  return !classified.shouldCrashBoundary && 
    (classified.category === 'network_error' || 
     classified.category === 'permission_error' ||
     classified.category === 'data_error');
}

// ============= Error Handling =============

export interface HandleErrorOptions {
  /** Feature/module name for logging */
  feature?: string;
  /** Function name for logging */
  functionName?: string;
  /** Show toast notification to user */
  showToast?: boolean;
  /** Custom user-friendly message */
  toastMessage?: string;
  /** Report to Sentry */
  reportToSentry?: boolean;
  /** Additional context for logging */
  context?: Record<string, unknown>;
  /** Rethrow after handling */
  rethrow?: boolean;
}

/**
 * Unified error handler for API calls
 */
export function handleApiError(
  error: unknown,
  options: HandleErrorOptions = {}
): ApiError {
  const {
    feature = 'api',
    functionName = 'unknown',
    showToast = true,
    toastMessage,
    reportToSentry = true,
    context = {},
    rethrow = false,
  } = options;

  const apiError = classifyError(error);

  // Log the error
  logger.error(`API Error: ${apiError.code}`, {
    context: {
      feature,
      function_name: functionName,
      ...context,
    },
    error: apiError.originalError || apiError,
    error_code: apiError.code,
    error_type: apiError.retryable ? 'network' : 'unexpected',
  });

  // Show toast to user
  if (showToast) {
    const message = toastMessage || apiError.message;
    
    if (apiError.code === 'rate_limited') {
      toast.warning(message, { duration: 5000 });
    } else if (apiError.retryable) {
      toast.error(message, {
        action: {
          label: 'Повторить',
          onClick: () => window.location.reload(),
        },
      });
    } else {
      toast.error(message);
    }
  }

  // Report to Sentry (skip for expected errors)
  if (reportToSentry && !['unauthorized', 'forbidden', 'not_found', 'rate_limited'].includes(apiError.code)) {
    Sentry.withScope((scope) => {
      scope.setTag('error_code', apiError.code);
      scope.setTag('feature', feature);
      scope.setExtras({
        functionName,
        statusCode: apiError.statusCode,
        retryable: apiError.retryable,
        ...context,
      });
      Sentry.captureException(apiError.originalError || apiError);
    });
  }

  if (rethrow) {
    throw apiError;
  }

  return apiError;
}

// ============= Wrapper for async functions =============

/**
 * Wraps an async function with unified error handling
 */
export function withApiErrorHandling<T extends (...args: unknown[]) => Promise<unknown>>(
  fn: T,
  options: Omit<HandleErrorOptions, 'rethrow'> = {}
): T {
  return (async (...args: Parameters<T>) => {
    try {
      return await fn(...args);
    } catch (error) {
      handleApiError(error, { ...options, rethrow: true });
    }
  }) as T;
}

// ============= Query/Mutation Error Handler =============

/**
 * Error handler for React Query onError callbacks
 */
export function queryErrorHandler(feature: string) {
  return (error: unknown) => {
    handleApiError(error, {
      feature,
      functionName: 'query',
      showToast: true,
      reportToSentry: true,
    });
  };
}

/**
 * Error handler for mutations
 */
export function mutationErrorHandler(feature: string, functionName: string) {
  return (error: unknown) => {
    handleApiError(error, {
      feature,
      functionName,
      showToast: true,
      reportToSentry: true,
    });
  };
}

// ============= Safe Handler Factory =============

/**
 * Create a non-throwing error handler for domain operations
 */
export function createSafeHandler<T>(
  defaultValue: T,
  errorCode: string,
  onError?: (error: ClassifiedError) => void
) {
  return async function safeHandler(fn: () => Promise<T>): Promise<T> {
    try {
      return await fn();
    } catch (error) {
      const classified = classifyErrorForBoundary(error);
      classified.errorCode = errorCode;
      onError?.(classified);
      return defaultValue;
    }
  };
}
